import { getNestedValue } from './field-accessor.util';
import { FilterState } from '../interfaces/filter-state.interface';
import { ColDef } from '../interfaces/col-def.interface';

/**
 * Utility functions for filtering data
 */

/**
 * Filter array by a search term across multiple fields
 *
 * @param data - Array to filter
 * @param searchTerm - Search term
 * @param searchableFields - Fields to search in (if not provided, searches all)
 * @param caseSensitive - Whether search is case sensitive
 * @returns Filtered array
 */
export function filterBySearchTerm<T>(
  data: T[],
  searchTerm: string,
  searchableFields?: string[],
  caseSensitive: boolean = false
): T[] {
  if (!data || data.length === 0 || !searchTerm || searchTerm.trim() === '') {
    return data;
  }

  const term = caseSensitive ? searchTerm : searchTerm.toLowerCase();

  return data.filter(row => {
    // If specific fields provided, only search those
    if (searchableFields && searchableFields.length > 0) {
      return searchableFields.some(field => {
        const value = getNestedValue(row, field);
        return matchesSearchTerm(value, term, caseSensitive);
      });
    }

    // Otherwise, search all string values in the object
    return searchInObject(row, term, caseSensitive);
  });
}

/**
 * Filter array by column filters (Excel-like multi-select filters)
 *
 * @param data - Array to filter
 * @param filterState - Filter state (field -> selected values)
 * @param columnDefs - Optional column definitions for custom filter functions
 * @returns Filtered array
 */
export function filterByColumnFilters<T>(
  data: T[],
  filterState: FilterState<T>,
  columnDefs?: ColDef<T>[]
): T[] {


  if (!data || data.length === 0 || !filterState || Object.keys(filterState).length === 0) {
    return data;
  }

  return data.filter(row => {
    // Row must match ALL column filters (AND logic)
    return Object.entries(filterState).every(([field, filterValue]) => {
      // Skip empty filters
      if (filterValue === null || filterValue === undefined) {
        return true;
      }

      // Find column definition for this field
      const colDef = columnDefs?.find(col => col.field === field);
      const customFilterFn = colDef?.filterOptions?.customFilterFn;
      const valueExtractor = colDef?.filterOptions?.valueExtractor;


      // If custom filter function is provided, use it
      if (customFilterFn) {
        // Handle array of selected values (multi-select) with custom function
        if (Array.isArray(filterValue)) {
          if (filterValue.length === 0) {
            return true;
          }
          const result = filterValue.some(selectedValue => {
            return customFilterFn(row, selectedValue);
          });
          return result;
        }
        // Handle single value with custom function
        return customFilterFn(row, filterValue);
      }

      // If valueExtractor is provided, use it to extract the value for comparison
      if (valueExtractor) {
        const extractedValue = valueExtractor(row);
      

        // Handle array of selected values (multi-select)
        if (Array.isArray(filterValue)) {
          if (filterValue.length === 0) {
            return true;
          }
          const result = filterValue.some(selectedValue => {
            const match = String(extractedValue) === String(selectedValue);
          
            return match;
          });
          return result;
        }

        // Handle single value
        return String(extractedValue) === String(filterValue);
      }

      // Otherwise, use default matching logic with field path
      const rowValue = getNestedValue(row, field);

      // Handle array of selected values (multi-select)
      if (Array.isArray(filterValue)) {
        if (filterValue.length === 0) {
          return true;
        }
        return filterValue.some(selectedValue =>
          valuesMatch(rowValue, selectedValue)
        );
      }

      // Handle single value
      return valuesMatch(rowValue, filterValue);
    });
  });
}

/**
 * Check if value matches search term
 */
function matchesSearchTerm(value: any, searchTerm: string, caseSensitive: boolean): boolean {
  if (value === null || value === undefined) {
    return false;
  }

  const stringValue = String(value);
  const valueToSearch = caseSensitive ? stringValue : stringValue.toLowerCase();

  return valueToSearch.includes(searchTerm);
}

/**
 * Search recursively in object
 */
function searchInObject(obj: any, searchTerm: string, caseSensitive: boolean, depth: number = 0): boolean {
  if (depth > 5) return false; // Prevent infinite recursion

  if (obj === null || obj === undefined) {
    return false;
  }

  // Check primitive values
  if (typeof obj !== 'object') {
    return matchesSearchTerm(obj, searchTerm, caseSensitive);
  }

  // Skip Date objects
  if (obj instanceof Date) {
    return matchesSearchTerm(obj.toISOString(), searchTerm, caseSensitive);
  }

  // Skip Arrays
  if (Array.isArray(obj)) {
    return obj.some(item => searchInObject(item, searchTerm, caseSensitive, depth + 1));
  }

  // Search in object properties
  return Object.values(obj).some(value =>
    searchInObject(value, searchTerm, caseSensitive, depth + 1)
  );
}

/**
 * Check if two values match (for filtering)
 */
function valuesMatch(rowValue: any, filterValue: any): boolean {
  // Handle null/undefined
  if (rowValue === null || rowValue === undefined) {
    return filterValue === null || filterValue === undefined || filterValue === '';
  }

  // Handle objects (compare by reference or stringify)
  if (typeof rowValue === 'object' && typeof filterValue === 'object') {
    // For objects, compare stringified versions
    return JSON.stringify(rowValue) === JSON.stringify(filterValue);
  }

  // Handle dates
  if (rowValue instanceof Date && filterValue instanceof Date) {
    return rowValue.getTime() === filterValue.getTime();
  }

  // Handle primitive values (loose equality)
  return String(rowValue) === String(filterValue);
}

/**
 * Get unique values for a field (for filter options)
 *
 * @param data - Array of data
 * @param field - Field path
 * @param sortValues - Whether to sort the unique values
 * @returns Array of unique values
 */
export function getUniqueValues<T>(
  data: T[],
  field: string,
  sortValues: boolean = true
): any[] {
  if (!data || data.length === 0) {
    return [];
  }

  const valuesSet = new Set<any>();

  data.forEach(row => {
    const value = getNestedValue(row, field);

    // Skip null/undefined
    if (value !== null && value !== undefined) {
      // For objects, use stringified version as key
      if (typeof value === 'object' && !(value instanceof Date)) {
        valuesSet.add(JSON.stringify(value));
      } else {
        valuesSet.add(value);
      }
    }
  });

  let uniqueValues = Array.from(valuesSet);

  // Parse back objects
  uniqueValues = uniqueValues.map(val => {
    try {
      return JSON.parse(val);
    } catch {
      return val;
    }
  });

  // Sort if requested
  if (sortValues) {
    uniqueValues.sort((a, b) => {
      if (a === null || a === undefined) return -1;
      if (b === null || b === undefined) return 1;

      // Try as numbers
      const numA = Number(a);
      const numB = Number(b);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }

      // Try as dates
      if (a instanceof Date && b instanceof Date) {
        return a.getTime() - b.getTime();
      }

      // Default to string comparison
      return String(a).localeCompare(String(b));
    });
  }

  return uniqueValues;
}

/**
 * Get filter options with counts for a field
 *
 * @param data - Array of data
 * @param field - Field path
 * @param currentFilters - Current filter state (to show counts for filtered data)
 * @param displayNameGetter - Optional custom function to get display name for a value
 * @param valueExtractor - Optional function to extract value from complex objects
 * @param columnDefs - Optional column definitions (needed for valueExtractor support)
 * @returns Array of {value, label, count}
 */
export function getFilterOptionsWithCounts<T>(
  data: T[],
  field: string,
  currentFilters?: FilterState<T>,
  displayNameGetter?: (value: any) => string,
  valueExtractor?: (item: T) => any,
  columnDefs?: ColDef<T>[]
): Array<{ value: any; label: string; count: number }> {
  if (!data || data.length === 0) {
    return [];
  }

  // Apply current filters (excluding the field we're getting options for)
  let filteredData = data;
  if (currentFilters) {
    const filtersWithoutCurrentField = { ...currentFilters };
    delete filtersWithoutCurrentField[field];

    if (Object.keys(filtersWithoutCurrentField).length > 0) {
      // CRITICAL: Pass columnDefs so valueExtractor is used when filtering
      // This ensures consistency between option generation and data filtering
      filteredData = filterByColumnFilters(data, filtersWithoutCurrentField, columnDefs);
    }
  }

  // Count occurrences - use Map to store both value and count
  const countsMap = new Map<string, { value: any; count: number }>();

  filteredData.forEach(row => {
    // Use valueExtractor if provided, otherwise use field path
    const value = valueExtractor
      ? valueExtractor(row)
      : getNestedValue(row, field);

    // Create serialized key for Map (handles objects and primitives)
    const key = typeof value === 'object' && value !== null
      ? JSON.stringify(value)
      : String(value);

    if (countsMap.has(key)) {
      // Increment count for existing value
      const existing = countsMap.get(key)!;
      existing.count++;
    } else {
      // Add new value with count of 1
      countsMap.set(key, { value, count: 1 });
    }
  });

  // Convert to array and sort by count (descending)
  const options = Array.from(countsMap.values())
    .map(({ value, count }) => {
      // Use custom displayNameGetter if provided, otherwise fallback to String(value)
      let label: string;
      if (displayNameGetter) {
        label = displayNameGetter(value);
      } else {
        label = value !== null && value !== undefined ? String(value) : '(Vacío)';
      }

      return {
        value,
        label,
        count,
      };
    })
    .sort((a, b) => b.count - a.count);

  return options;
}

/**
 * Check if filter state is empty
 */
export function isFilterEmpty<T>(filterState: FilterState<T>): boolean {
  if (!filterState) return true;

  return Object.values(filterState).every(value => {
    if (value === null || value === undefined) return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    return false;
  });
}

/**
 * Clear filter for a specific field
 */
export function clearFieldFilter<T>(filterState: FilterState<T>, field: string): FilterState<T> {
  const newState = { ...filterState };
  delete newState[field];
  return newState;
}

/**
 * Clear all filters
 */
export function clearAllFilters<T>(): FilterState<T> {
  return {} as FilterState<T>;
}

/**
 * Merge filter states
 */
export function mergeFilterStates<T>(
  state1: FilterState<T>,
  state2: FilterState<T>
): FilterState<T> {
  return { ...state1, ...state2 };
}

/**
 * Get active filter count
 */
export function getActiveFilterCount<T>(filterState: FilterState<T>): number {
  if (!filterState) return 0;

  return Object.values(filterState).filter(value => {
    if (value === null || value === undefined) return false;
    if (Array.isArray(value) && value.length === 0) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    return true;
  }).length;
}
